import { useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '../api/client';
import type { Automation, AutomationRun, RetentionConfig } from '../api/types';

/** Interval choices offered by the create/edit forms. */
export const INTERVAL_PRESETS = [
  { minutes: 60, label: 'Hourly' },
  { minutes: 360, label: 'Every 6 hours' },
  { minutes: 1440, label: 'Daily' },
  { minutes: 10080, label: 'Weekly' },
] as const;

/** Human label for an interval, falling back to a generic description for non-presets. */
export function intervalLabel(minutes: number): string {
  const preset = INTERVAL_PRESETS.find((p) => p.minutes === minutes);
  if (preset) return preset.label;
  return minutes % 1440 === 0 ? `Every ${minutes / 1440} days` : `Every ${minutes} min`;
}

/** Compact one-line rule summary for tables, e.g. 'keep newest 10 · delete > 30 d'. */
export function ruleSummary(a: Automation): string {
  const cfg = a.config as RetentionConfig;
  const parts: string[] = [];
  if (cfg.keepNewest) parts.push(`keep newest ${cfg.keepNewest}`);
  if (cfg.olderThanDays) parts.push(`delete > ${cfg.olderThanDays} d`);
  return parts.join(' · ') || '—';
}

/** Colored status badge for automation and run statuses. */
export function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-gray-400">never run</span>;
  const cls =
    status === 'ok' ? 'bg-green-100 text-green-800'
    : status === 'warning' ? 'bg-amber-100 text-amber-800'
    : status === 'running' ? 'bg-gray-100 text-gray-600'
    : 'bg-red-100 text-red-800';
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

/** On/off switch backed by a button, so its visual state can move instantly with an optimistic update. */
export function ToggleSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(ev) => {
        ev.stopPropagation();
        onChange(!checked);
      }}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-green-600' : 'bg-gray-300'} ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

/** Result panel for a just-finished manual run or dry-run preview. */
export function RunResult({ run }: { run: AutomationRun }) {
  return (
    <div className={`rounded border p-3 text-sm ${run.status === 'error' ? 'border-red-200 bg-red-50' : run.status === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
      <div className="font-medium">
        {run.dry_run ? 'Preview (dry run): would delete' : 'Deleted'} {run.affected} of {run.matched} matched ({run.scanned} scanned)
      </div>
      {run.error ? <div className="mt-1 text-xs">{run.error}</div> : null}
      {run.sampleKeys.length > 0 ? (
        <ul className="mt-2 max-h-48 overflow-auto font-mono text-xs text-gray-700">
          {run.sampleKeys.map((k) => (
            <li key={k}>{k}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Run history table for one automation; fetches its own data. */
export function RunHistory({ automationId }: { automationId: string }) {
  const runs = useQuery({
    queryKey: ['automation-runs', automationId],
    queryFn: () => api<{ runs: AutomationRun[] }>(`/api/admin/automations/${automationId}/runs`),
  });

  return (
    <div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-gray-500">
            <th className="p-1.5">Started</th>
            <th className="p-1.5">Trigger</th>
            <th className="p-1.5">Status</th>
            <th className="p-1.5 text-right">Scanned</th>
            <th className="p-1.5 text-right">Matched</th>
            <th className="p-1.5 text-right">Deleted</th>
            <th className="p-1.5">Detail</th>
          </tr>
        </thead>
        <tbody>
          {(runs.data?.runs ?? []).map((r) => (
            <tr key={r.id} className="border-b align-top">
              <td className="p-1.5 whitespace-nowrap">{new Date(r.started_utc).toLocaleString()}</td>
              <td className="p-1.5">
                {r.trigger}
                {r.dry_run ? <span className="ml-1 rounded bg-gray-200 px-1 text-[10px]">dry run</span> : null}
              </td>
              <td className="p-1.5"><StatusPill status={r.status} /></td>
              <td className="p-1.5 text-right">{r.scanned}</td>
              <td className="p-1.5 text-right">{r.matched}</td>
              <td className="p-1.5 text-right">{r.affected}</td>
              <td className="p-1.5 text-gray-600">
                {r.error ?? (r.sampleKeys.length > 0 ? <span className="font-mono">{r.sampleKeys[0]}{r.sampleKeys.length > 1 ? ` +${r.sampleKeys.length - 1} more` : ''}</span> : '—')}
              </td>
            </tr>
          ))}
          {runs.data && runs.data.runs.length === 0 ? (
            <tr>
              <td className="p-2 text-gray-500" colSpan={7}>No runs yet. Use Preview to see what this automation would do.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {runs.isLoading ? <div className="p-2 text-xs">Loading…</div> : null}
      {runs.isError ? <div className="p-2 text-xs text-red-600">{getErrorMessage(runs.error)}</div> : null}
    </div>
  );
}
